// src/types/onboarding.ts

// Mirroring potential structure in user_settings for consistency
export interface CallActivationSettings {
  confirm_appointments: { enabled: boolean };
  recover_cancellations: { enabled: boolean };
  reschedule_noshows: { enabled: boolean };
  // Add timing settings here if configurable during onboarding
  // For now, assuming default timings are handled by backend/settings page
}

export interface DayHours {
  enabled: boolean;
  start: string; // e.g., "09:00"
  end: string; // e.g., "17:00"
}

export interface AICallingHours {
  timezone: string;
  days: {
    Monday: DayHours;
    Tuesday: DayHours;
    Wednesday: DayHours;
    Thursday: DayHours;
    Friday: DayHours;
    Saturday: DayHours;
    Sunday: DayHours;
  };
  useBusinessHours?: boolean; // Flag to indicate if using business hours
}

// Define possible provisioning statuses from backend
export type ProvisioningStatus =
  | "idle"
  | "checking_availability"
  | "availability_checked"
  | "provisioning_started"
  | "searching_twilio_number" // Matched to backend status
  | "purchasing_twilio_number" // Matched to backend status
  | "configuring_vapi_assistant" // Matched to backend status
  | "registering_number_with_vapi" // Matched to backend status
  | "releasing_twilio_number_on_error" // Matched to backend status
  | "success"
  | "failed";

// NEW: Interface for Bookable Service with Duration
export interface BookableService {
  name: string;
  duration: number; // Duration in minutes
  description?: string;
}

export interface OnboardingData {
  businessName: string;
  aiGreetingMessage: string;
  replyToEmail: string;
  aiVoiceId: string; // ID of the selected voice
  aiVoiceProvider?: string; // Optional: Provider if needed
  aiName?: string; // Name associated with the voice/avatar
  areaCode: string; // 3-digit area code for VAPI number provisioning
  forwardingNumber: string; // E.164 format
  businessHoursRaw: string; // Raw text input
  bookableServices: BookableService[]; // CORRECTED: Array of BookableService objects
  businessAddress: string;
  knowledgeBaseUrls: string[]; // Array of URLs - will always be empty from onboarding
  calendarProvider:
    | "google"
    | "acuity"
    | "calendly"
    | "square"
    | "outlook"
    | "ghl"
    | null; // Track which calendar is connected
  calendarEmail: string | null; // Email of connected calendar
  callActivationSettings: CallActivationSettings;
  aiCallingHours: AICallingHours;
  hipaa_enabled: boolean;
  ai_recording_preference: boolean;
  defaultAppointmentDurationMinutes: number; // NEW: For user_settings

  isAreaCodeAvailable: boolean | null;
  areaCodeCheckStatus: "idle" | "checking" | "checked";
  provisioningStatus: ProvisioningStatus;
  provisioningError: string | null;
  assignedPhoneNumber?: string | null;
}

export const defaultAICallingHours: AICallingHours = {
  timezone:
    Intl.DateTimeFormat().resolvedOptions().timeZone || "America/New_York",
  days: {
    Monday: { enabled: true, start: "09:00", end: "17:00" },
    Tuesday: { enabled: true, start: "09:00", end: "17:00" },
    Wednesday: { enabled: true, start: "09:00", end: "17:00" },
    Thursday: { enabled: true, start: "09:00", end: "17:00" },
    Friday: { enabled: true, start: "09:00", end: "17:00" },
    Saturday: { enabled: false, start: "09:00", end: "17:00" },
    Sunday: { enabled: false, start: "09:00", end: "17:00" },
  },
  useBusinessHours: false, // Default to not using business hours, showing custom instead
};

export const defaultCallActivationSettings: CallActivationSettings = {
  confirm_appointments: { enabled: true },
  recover_cancellations: { enabled: false },
  reschedule_noshows: { enabled: false },
};

export const initialOnboardingData: OnboardingData = {
  businessName: "",
  aiGreetingMessage: "",
  replyToEmail: "",
  aiVoiceId: "",
  aiName: "",
  areaCode: "",
  forwardingNumber: "",
  businessHoursRaw:
    "Monday-Friday: 9 AM - 5 PM\nSaturday: 10 AM - 2 PM\nSunday: Closed",
  bookableServices: [],
  businessAddress: "",
  knowledgeBaseUrls: [], // Always initialize as empty, no longer collected
  calendarProvider: null,
  calendarEmail: null,
  callActivationSettings: defaultCallActivationSettings,
  aiCallingHours: defaultAICallingHours,
  hipaa_enabled: false,
  ai_recording_preference: true,
  defaultAppointmentDurationMinutes: 30,
  assignedPhoneNumber: null,
  isAreaCodeAvailable: null,
  areaCodeCheckStatus: "idle",
  provisioningStatus: "idle",
  provisioningError: null,
};

export interface OnboardingStepProps {
  data: OnboardingData;
  updateData: (update: Partial<OnboardingData>) => void;
  goToNextStep: () => void;
  goToPrevStep?: () => void;
  setStep: (step: number) => void;
  triggerSave?: () => Promise<boolean>;
  isSaving?: boolean;
}